import { getDomain } from '@/helpers/getDomains';
import { type ErrorResponse } from '@/types';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { type EventSourceMessage, fetchEventSource } from '@fortaine/fetch-event-source';

type Params = {
  requestBody?: object | null;
  path: string;
  /** Gets the HTTP status of a 2xx response; any other status rejects. */
  onopen?: (status: number) => void;
  onmessage?: (ev: EventSourceMessage) => void;
  onclose?: () => void;
  onAbort?: () => void;
  // onerror?: (error: string) => void;
  endPointVersion?: 'v1' | 'v2' | 'v2-1';
  abortController: AbortController;
};

/** The error a non-2xx stream rejects with. */
type StreamHttpError = Error & { status: number };

// The message is the backend `detail.type`, or `'StreamHttpError'` when there
// is none (e.g. a WAF page).
const readStreamHttpError = async (response: Response): Promise<StreamHttpError> => {
  let detail: Partial<ErrorResponse['detail']> | undefined;
  try {
    const parsed = JSON.parse(await response.text()) as { detail?: unknown };
    if (parsed.detail && typeof parsed.detail === 'object') {
      detail = parsed.detail;
    }
  } catch {
    detail = undefined;
  }
  const type = typeof detail?.type === 'string' ? detail.type : 'StreamHttpError';
  // `detail.msg` is free-form backend text, so it is not carried.
  return Object.assign(new Error(type), { status: response.status });
};

// A SyntaxError's message quotes the chunk, which can be clinical text, so the
// parse failure is rethrown without it.
const parseJsonChunk = (data: string): string => {
  try {
    return (JSON.parse(data) as { data: string }).data;
  } catch {
    throw Object.assign(new Error('StreamPayloadParseError'), {
      name: 'StreamPayloadParseError',
    });
  }
};

export const customFetchEventSource = (param: Params) => {
  const endPointVersion = param.endPointVersion || 'v2';
  const sessionURL = getDomain(endPointVersion) + param.path;
  const token = getAuthTokenForHttpClientSync();

  const requestHeaders: Record<string, string> = {
    ...buildScribeSseHeaders(token),
  };

  //   const requestBody:

  let transformedChar = '';
  const isJsonStream =
    endPointVersion === 'v2-1' || param.path.includes('live-dictation');

  const ctrl = param.abortController;

  //   wrap it in promise
  const promise = new Promise<string>((resolve, reject) => {
    let failed = false;
    const abortHandler = () => {
      failed = true;
      reject(new Error('StreamManuallyAbortedError'));
      param.onAbort?.();
    };
    ctrl.signal.addEventListener('abort', abortHandler);
    // Reject with the error itself; the listener goes first so the abort
    // does not also count as a manual one.
    const fail = (error: unknown) => {
      failed = true;
      ctrl.signal.removeEventListener('abort', abortHandler);
      reject(error instanceof Error ? error : new Error(String(error)));
      ctrl.abort();
    };

    void fetchEventSource(sessionURL, {
      headers: requestHeaders,
      body: JSON.stringify(param.requestBody),
      method: 'POST',
      openWhenHidden: true,
      signal: ctrl.signal,

      onopen: async (response) => {
        if (!response.ok) {
          // Throw, not abort: onerror then rejects with this error. An abort would
          // reject as StreamManuallyAbortedError and count as a cancel.
          throw await readStreamHttpError(response);
        }
        param.onopen?.(response.status);
      },
      onmessage: (ev) => {
        // Accumulate now so `resolve` has every chunk; only the callback is
        // deferred. A parse error throws into the library, which rejects.
        // An empty event on a JSON stream (a keep-alive) carries no chunk.
        if (isJsonStream && ev.data === '') return;
        let message = ev;
        if (isJsonStream) {
          message = { ...ev, data: parseJsonChunk(ev.data) };
          transformedChar += message.data;
        } else {
          transformedChar += ev.data === '' ? '\n' : ev.data.replace(/ {3}/g, '\n');
        }

        setTimeout(() => {
          try {
            param.onmessage?.(message);
          } catch (error) {
            fail(error);
          }
        }, 0);
      },
      onclose: () => {
        // Close after the deferred onmessage callbacks already queued: the
        // caller sees every chunk first, and a throw from one fails the stream
        // instead of closing it.
        setTimeout(() => {
          if (failed) return;
          ctrl.signal.removeEventListener('abort', abortHandler);
          try {
            param.onclose?.();
          } catch (error) {
            fail(error);
            return;
          }
          resolve(transformedChar);
          ctrl?.abort();
        }, 0);
      },
      onerror: (error) => {
        fail(error);
        throw error;
      },
    });
  });

  return promise;
};
